import React, { useEffect, useState } from 'react';
import { AuthUser, User, Location, WorkLocation } from '../types';
import { signOutUser } from '../firebase/auth';
import { getOrCreateUser, getLatestCheckIn, createCheckIn, getAllWorkLocations } from '../firebase/firestore';
import { getCurrentLocationWithAddress, isWithinAnyWorkRange, isTodayInTurkey } from '../utils';
import CheckInButton from './CheckInButton';
import LocationStatus from './LocationStatus';
import CheckInHistory from './CheckInHistory';
import LocationMap from './LocationMap';

interface DashboardProps {
  user: AuthUser;
}

const Dashboard: React.FC<DashboardProps> = ({ user }) => {
  const [userData, setUserData] = useState<User | null>(null);
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null);
  const [workLocations, setWorkLocations] = useState<WorkLocation[]>([]);
  const [nearestLocation, setNearestLocation] = useState<WorkLocation | null>(null);
  const [locationError, setLocationError] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [hasCheckedInToday, setHasCheckedInToday] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        if (user?.uid) {
          // This will create a user if they don't exist
          const userInfo = await getOrCreateUser(user);
          setUserData(userInfo);
          
          // Check if this is a new user (has default work location)
          if (userInfo.workLocation.latitude === 0 && userInfo.workLocation.longitude === 0) {
            setIsNewUser(true);
          }
          
          if (userInfo) {
            const latestCheckIn = await getLatestCheckIn(user.uid);
            // Check if the latest check-in was today in Turkey timezone
            if (latestCheckIn && isTodayInTurkey(latestCheckIn.timestamp)) {
              setHasCheckedInToday(true);
            }
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user]);

  useEffect(() => {
    const loadWorkLocations = async () => {
      try {
        const locations = await getAllWorkLocations();
        setWorkLocations(locations);
      } catch (error) {
        console.error('Error loading work locations:', error);
      }
    };

    loadWorkLocations();
  }, []);

  useEffect(() => {
    const getLocation = async () => {
      try {
        const location = await getCurrentLocationWithAddress();
        setCurrentLocation(location);
        setLocationError('');
      } catch (error: any) {
        setLocationError(error.message);
      }
    };

    getLocation();
  }, []);

  // Check which location user is nearest to when current location or work locations change
  useEffect(() => {
    if (currentLocation && workLocations.length > 0) {
      const result = isWithinAnyWorkRange(currentLocation, workLocations, 1); // 1km range
      setNearestLocation(result.nearestLocation as WorkLocation | null);
    } else {
      setNearestLocation(null);
    }
  }, [currentLocation, workLocations]);

  const handleCheckIn = async () => {
    if (!currentLocation || !userData) return;

    try {
      // Check if user is within range of any work location
      const result = isWithinAnyWorkRange(currentLocation, workLocations, 1); // 1km range
      
      if (!result.isWithinRange || !result.nearestLocation) {
        throw new Error('Herhangi bir iş yeri konumuna yeterince yakın değilsiniz');
      }
      
      await createCheckIn(
        user.uid, 
        currentLocation, 
        result.distance,
        result.nearestLocation.id,
        result.nearestLocation.name
      );
      setHasCheckedInToday(true);
      
      // Refresh check-in history
      // You could implement this to show recent check-ins
    } catch (error) {
      console.error('Error during check-in:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Kullanıcı bulunamadı</h2>
          <p className="text-gray-600">Lütfen yöneticinizle iletişime geçin.</p>
        </div>
      </div>
    );
  }

  const canCheckIn = currentLocation && workLocations.length > 0 ? 
    isWithinAnyWorkRange(currentLocation, workLocations, 1).isWithinRange : false;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <img 
                src="https://meconyapi.com/clients/logo-mecon.png" 
                alt="Mecon Logo" 
                className="h-16 w-auto max-w-xs"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Hoş geldiniz, {userData.name}</h1>
                <p className="text-gray-600">{userData.email}</p>
                {isNewUser && (
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-800">
                      👋 Hoş geldiniz! İş yeriniz konumu yöneticiniz tarafından ayarlanacak. 
                      Konum yapılandırıldıktan sonra giriş yapabileceksiniz.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Çıkış Yap
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Remaining PTO (Kalan İzin) - compact badge above Konum Haritası */}
          {typeof userData.pto === 'number' && (
            <div className="mb-4">
              <span className="inline-flex items-center rounded-md bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                Kalan İzin: {userData.pto} gün
              </span>
            </div>
          )}

          {/* Location Map */}
          <div className="bg-white overflow-hidden shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Konum Haritası
              </h3>
              <LocationMap
                currentLocation={currentLocation}
                workLocation={userData.workLocation}
                allWorkLocations={workLocations}
                nearestLocation={nearestLocation}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Check-in Button */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Giriş Durumu
                </h3>
                <CheckInButton
                  canCheckIn={canCheckIn}
                  hasCheckedInToday={hasCheckedInToday}
                  onCheckIn={handleCheckIn}
                  loading={loading}
                />
              </div>
            </div>

            {/* Location Status */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Konum Durumu
                </h3>
                <LocationStatus
                  currentLocation={currentLocation}
                  workLocation={userData.workLocation}
                  allWorkLocations={workLocations}
                  nearestLocation={nearestLocation}
                  locationError={locationError}
                  canCheckIn={canCheckIn}
                />
              </div>
            </div>
          </div>

          {/* Check-in History */}
          <div className="mt-6 bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Son Girişler
              </h3>
              <CheckInHistory userId={user.uid} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard; 